import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { mediaUrl } from '../utils/mediaUrl';

// Matches HomeWrapper on the main page: full-bleed color sections (like
// SectionWrapper and ClosingSection below) get inset from the viewport
// edges instead of running edge-to-edge, so this page reads the same way
// the home page does rather than looking like a different site.
const PageWrapper = styled.div`
  padding-left: 2rem;
  padding-right: 2rem;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Crumb = styled.nav`
  max-width: 1700px;
  margin: 0 auto;
  padding: 1.6rem 2rem 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);

  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  span.sep { margin: 0 0.4rem; }
  span.current { color: #fff; font-weight: bold; }
`;

const SectionWrapper = styled.section`
  background-color: #90C4D9;
  padding: 70px 20px 40px;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    padding: 40px 0px 0px;
  }
`;

const SectionTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 3rem;
  margin-bottom: 10px;
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SectionSubTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 1.8rem;
  margin-bottom: 70px;
  @media (max-width: 1024px) {
    margin-bottom: 20px;
  }
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const EventWrapper = styled.div`
  background: transparent;
  padding: 20px;
  margin-bottom: 10px;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1700px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  padding-right: 20px;

  @media (max-width: 1024px) {
    padding-right: 0;
    text-align: center;
    max-width: 600px;
    width: 100%;
  }
`;

const MiddleColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1024px) {
    max-width: 600px;
    width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 20px;

  @media (max-width: 1024px) {
    padding-left: 0;
    max-width: 600px;
    width: 100%;
  }
`;

const EventTitle = styled.h1`
  font-size: 3rem;
  margin-top: 40px;
  margin-bottom: 0px;
  display: inline-block;
  padding-bottom: 6px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);
  background-size: 100% 4px;
  background-position: 0 bottom;
  background-repeat: no-repeat;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-image: linear-gradient(to right, transparent 50%, #90C4D9 50%);
    background-size: 8px 4px;
    background-repeat: repeat-x;
  }

  @media (max-width: 1024px) {
    font-size: 1.6rem;
    margin-top: 10px;
    text-align: center;
  }
`;

const EventMeta = styled.p`
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 0.6rem;
  margin-bottom: 0;

  @media (max-width: 1024px) {
    font-size: 0.95rem;
  }
`;

const SecondaryHeadline = styled.h4`
  font-size: 1.8rem;
  font-weight: bold;
  margin-top: 40px;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1.4rem;
    margin-top: 20px;
    margin-bottom: 10px;
  }
`;

const BulletList = styled.ul`
  list-style-type: disc;
  padding-left: 20px;
  margin-bottom: 20px;
  font-size: 1.4rem;

  @media (max-width: 1024px) {
    display: inline-block;
    text-align: left;
    font-size: 1rem;
    margin-bottom: 10px;
  }
`;

const TextContent = styled.p`
  font-size: 1.4rem;
  line-height: 1.5;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1rem;
  }
`;

const EventImage = styled.img`
  max-width: 100%;
  height: auto;
  margin-bottom: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
`;

const EventButton = styled.button`
  background-color: #47ABDB;
  text-decoration: none;
  height: 4.0rem;
  font-weight: bold;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 1.8rem;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  max-width: 300px;
  text-align: center;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: #0056b3;
  }
`;

const ProgramButton = styled.button`
  background-color: #47ABDB;
  text-decoration: none;
  height: 4.0rem;
  font-weight: bold;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 1.8rem;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  max-width: 300px;
  text-align: center;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: #0056b3;
  }
`;

// "SUBMIT ABSTRACT" is now the only label this button ever shows (English-only,
// no more German "BEITRAG EINREICHEN" overflow to work around) - same size as
// ProgramButton/EventButton, just forced to one line.
const SubmitAbstractButton = styled(ProgramButton)`
  white-space: nowrap;
  padding-left: 0;
  padding-right: 0;
`;

const EventInfo = styled.div`
  font-size: 1.4rem;
  margin-bottom: 40px;
  text-align: left;
  @media (max-width: 1024px) {
    font-size: 1rem;
  }
`;

const EventButtonTitle = styled.p`
  margin-bottom: 0.1rem;
  width: 100%;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const EventDetails = styled.p`
  margin: 0;
  width: 100%;
  margin-top: 0.2rem;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const SubText = styled.p`
  font-weight: bold;
  font-size: 1.4rem;
  line-height: 1.5;
  margin-top: 40px;
  padding: 0 40px 0 0;
  @media (max-width: 1024px) {
    font-size: 1rem;
    padding: 0;
    text-align: center;
  }
`;

const PastNote = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 300px;
  margin-bottom: 1.5rem;
`;

const SpeakersSection = styled.section`
  max-width: 1700px;
  margin: 0 auto;
  padding: 70px 40px 20px;
`;

const SpeakersTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 3rem;
  margin-bottom: 40px;
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
`;

const SpeakerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2.5rem 3rem;
`;

const Speaker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 190px;
`;

const AvatarPhoto = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 0.9rem;
`;

const AvatarInitials = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  margin-bottom: 0.9rem;
  background: #47ABDB;
  color: white;
  font-weight: bold;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpeakerName = styled.div`
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
`;

const SpeakerRole = styled.div`
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.9rem;
  margin-top: 0.2rem;
`;

const SpeakerTalk = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.92rem;
  font-style: italic;
  margin-top: 0.6rem;
  line-height: 1.4;
`;

// Optional free-text sections above / below the speakers roster - see the
// "Text Above/Below Speakers" fields in the event admin form. The accent bar
// echoes the gradient underline on EventTitle so these read as part of the
// same page rather than a bolted-on paragraph.
const BufferSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 56px 40px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 36px 24px;
  }
`;

const BufferAccent = styled.div`
  width: 70px;
  height: 4px;
  margin: 0 auto 28px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);

  @media (max-width: 768px) {
    margin-bottom: 18px;
  }
`;

const BufferText = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1.7;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

// The event's long-form content (textHeadline block and subText) sits below
// the hero container so the hero stays a compact promo card like the
// homepage; the body text gets room on the page background.
const EventContentSection = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 40px 10px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 36px 24px 0;
  }
`;

// The below-speakers text sits directly above the footer, which shares the
// same page-wide background gradient - without a container it visually
// bled straight into it. Rather than inventing a new container style, this
// reuses the site's actual convention for separating sections: a full-bleed
// flat-color band, the same #90C4D9 as the hero SectionWrapper above and the
// same bold centered-white treatment as SectionSubTitle.
const ClosingSection = styled.section`
  background-color: #90C4D9;
  padding: 70px 20px;
  margin-top: 60px;
  margin-bottom: 90px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 40px 20px;
    margin-top: 30px;
    margin-bottom: 50px;
  }
`;

const ClosingInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ClosingText = styled.p`
  color: white;
  font-weight: bold;
  font-size: 1.7rem;
  line-height: 1.5;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const NotFoundWrapper = styled.div`
  max-width: 700px;
  margin: 6rem auto;
  padding: 0 2rem;
  text-align: center;
  color: white;

  a {
    color: #47ABDB;
  }
`;

// Initials fallback when a speaker has no photo on file - "Elena Marchetti" -> "EM".
const initialsFor = (fullName) => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const EventDetail = ({ openRegistrationModal }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);

  const [event, setEvent] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const getContent = (defaultContent, enContent) => {
    return language === 'en' && enContent ? enContent : (defaultContent || '');
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setEvent(null);
    setSpeakers([]);

    axios.get(`${process.env.REACT_APP_API_URL}/api/cms/event/public/get-by-slug/${slug}`)
      .then((response) => {
        if (cancelled) return;
        setEvent(response.data);
        setLoading(false);
        return axios.get(`${process.env.REACT_APP_API_URL}/api/cms/event/public/${response.data.id}/speakers`)
          .then((speakersResponse) => {
            if (!cancelled) setSpeakers(speakersResponse.data || []);
          })
          .catch(() => {
            // Missing speakers shouldn't take the whole page down - the
            // section simply won't render.
          });
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!event) return undefined;
    const previousTitle = document.title;
    const headline = getContent(event.mainHeadline, event.mainHeadlineEN) || getContent(event.bigHeadline, event.bigHeadlineEN);
    document.title = headline ? `${headline} - HEARTS Conference` : 'HEARTS Conference';
    return () => { document.title = previousTitle; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, language]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleProgramClick = (e) => {
    e.preventDefault();
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const fullUrl = `${apiUrl}${event.programUrl || ''}`;
    if (event.programUrl) {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleRegisterClick = () => {
    if (typeof openRegistrationModal === 'function') {
      openRegistrationModal(event);
    }
  };

  const handleSubmitAbstractClick = () => {
    navigate(`/call-for-abstracts?eventId=${event.id}`);
  };

  if (loading) return null;

  if (notFound || !event) {
    return (
      <NotFoundWrapper>
        <h2>{t('event.notFound.title', 'Event not found')}</h2>
        <p>{t('event.notFound.body', "This event doesn't exist, or is no longer available.")}</p>
        <Link to="/">{t('event.notFound.link', 'Back to the homepage')}</Link>
      </NotFoundWrapper>
    );
  }

  // No hardcoded placeholder here on purpose - an event with no headline
  // entered in the CMS yet should show no title, not invented copy.
  const displayName = getContent(event.mainHeadline, event.mainHeadlineEN)
    || getContent(event.bigHeadline, event.bigHeadlineEN)
    || '';
  const isPast = Boolean(event.dateTime) && new Date(event.dateTime) < new Date();
  const metaLine = [formatDate(event.dateTime), event.location].filter(Boolean).join(' - ');
  const hasSpeakers = speakers.length > 0;

  return (
    <PageWrapper>
      <Crumb aria-label="Breadcrumb">
        <Link to="/">{t('nav.home', 'Home')}</Link>
        {displayName && (
          <>
            <span className="sep">/</span>
            <span className="current">{displayName}</span>
          </>
        )}
      </Crumb>

      <SectionWrapper>
        {(event.bigHeadline || event.bigHeadlineEN) && (
          <SectionTitle>{getContent(event.bigHeadline, event.bigHeadlineEN)}</SectionTitle>
        )}
        {(event.subHeadline || event.subHeadlineEN) && (
          <SectionSubTitle>{getContent(event.subHeadline, event.subHeadlineEN)}</SectionSubTitle>
        )}

        <EventWrapper>
          <ContentWrapper>
            <LeftColumn>
              {displayName && <EventTitle>{displayName}</EventTitle>}
              {metaLine && <EventMeta>{metaLine}</EventMeta>}

              {event.bulletPointsHeadline && (
                <>
                  <SecondaryHeadline>
                    {getContent(event.bulletPointsHeadline.headline, event.bulletPointsHeadline.headlineEN)}
                  </SecondaryHeadline>
                  <BulletList>
                    {(getContent(event.bulletPointsHeadline.bulletPoints, event.bulletPointsHeadline.bulletPointsEN) || [])
                      .map((point, index) => <li key={index}>{point}</li>)}
                  </BulletList>
                </>
              )}
            </LeftColumn>

            <MiddleColumn>
              {event.mainMediaUrl && (
                <EventImage
                  src={`${process.env.REACT_APP_API_URL || ''}${event.mainMediaUrl}`}
                  alt={displayName}
                />
              )}
            </MiddleColumn>

            {(!isPast || event.programUrl || event.acceptsSubmissions) && (
              <RightColumn>
                {!isPast && (
                  <>
                    <EventButton onClick={handleRegisterClick}>
                      <FormattedMessage
                        id={event.requiresApproval ? 'event.register.pre' : 'event.register'}
                        defaultMessage={event.requiresApproval ? 'PRE-REGISTER' : 'REGISTER NOW'}
                      />
                    </EventButton>
                    <EventInfo>
                      {displayName && <EventButtonTitle>{displayName}</EventButtonTitle>}
                      {metaLine && <EventDetails>{metaLine}</EventDetails>}
                    </EventInfo>
                  </>
                )}
                {isPast && (
                  <PastNote>
                    {t('event.past.note', 'This event took place on {date}.', { date: formatDate(event.dateTime) })}
                  </PastNote>
                )}
                {event.programUrl && (
                  <>
                    <ProgramButton onClick={handleProgramClick}>
                      <FormattedMessage id="event.program" defaultMessage="PROGRAM" />
                    </ProgramButton>
                    {(event.programText || event.programTextEN) && (
                      <EventInfo>
                        <EventButtonTitle>{getContent(event.programText, event.programTextEN)}</EventButtonTitle>
                      </EventInfo>
                    )}
                  </>
                )}
                {event.acceptsSubmissions && (
                  <SubmitAbstractButton onClick={handleSubmitAbstractClick}>
                    <FormattedMessage id="event.submitAbstract" defaultMessage="SUBMIT ABSTRACT" />
                  </SubmitAbstractButton>
                )}
              </RightColumn>
            )}
          </ContentWrapper>
        </EventWrapper>
      </SectionWrapper>

      {(event.textAboveSpeakers || event.textAboveSpeakersEN) && (
        <BufferSection>
          <BufferAccent />
          <BufferText>{getContent(event.textAboveSpeakers, event.textAboveSpeakersEN)}</BufferText>
        </BufferSection>
      )}

      {hasSpeakers && (
        <SpeakersSection>
          <SpeakersTitle>{t('event.speakers.title', 'Speakers')}</SpeakersTitle>
          <SpeakerRow>
            {speakers.map((speaker, index) => {
              const roleLine = [speaker.title, speaker.institution].filter(Boolean).join(', ');
              return (
                <Speaker key={index}>
                  {speaker.photoUrl ? (
                    <AvatarPhoto
                      src={mediaUrl(speaker.photoUrl)}
                      alt={speaker.fullName}
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <AvatarInitials>{initialsFor(speaker.fullName)}</AvatarInitials>
                  )}
                  <SpeakerName>{speaker.fullName}</SpeakerName>
                  {roleLine && <SpeakerRole>{roleLine}</SpeakerRole>}
                  {speaker.talkTitle && <SpeakerTalk>&ldquo;{speaker.talkTitle}&rdquo;</SpeakerTalk>}
                </Speaker>
              );
            })}
          </SpeakerRow>
        </SpeakersSection>
      )}

      {(event.textHeadline || (event.subText || event.subTextEN)) && (
        <EventContentSection>
          {event.textHeadline && (
            <>
              <SecondaryHeadline>
                {getContent(event.textHeadline.headline, event.textHeadline.headlineEN)}
              </SecondaryHeadline>
              <TextContent>
                {getContent(event.textHeadline.textContent, event.textHeadline.textContentEN)}
              </TextContent>
            </>
          )}
          {(event.subText || event.subTextEN) && (
            <SubText>{getContent(event.subText, event.subTextEN)}</SubText>
          )}
        </EventContentSection>
      )}

      {(event.textBelowSpeakers || event.textBelowSpeakersEN) && (
        <ClosingSection>
          <ClosingInner>
            <BufferAccent />
            <ClosingText>{getContent(event.textBelowSpeakers, event.textBelowSpeakersEN)}</ClosingText>
          </ClosingInner>
        </ClosingSection>
      )}
    </PageWrapper>
  );
};

export default EventDetail;
